// Copyright (c) Subzero Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import type { IdentifierString } from "@wallet-standard/base";
import type { FrostConfig } from "../config";
import type { WalletEntity } from "../types";

/**
 * Gets all discovered wallets.
 *
 * @param config - Frost configuration
 * @returns Array of wallet entities
 */
export function getWallets(config: FrostConfig): WalletEntity[] {
  return Array.from(config.store.state.wallets.values());
}

/**
 * Gets wallets sorted by priority (higher first), then by last connected time.
 *
 * @param config - Frost configuration
 * @returns Sorted array of wallet entities
 */
export function getSortedWallets(config: FrostConfig): WalletEntity[] {
  return Array.from(config.store.state.wallets.values()).sort((a, b) => {
    const priorityDiff = (b.priority ?? 0) - (a.priority ?? 0);
    if (priorityDiff !== 0) return priorityDiff;
    return (b.lastConnectedAt ?? 0) - (a.lastConnectedAt ?? 0);
  });
}

/**
 * Gets a specific wallet by name.
 *
 * @param config - Frost configuration
 * @param walletName - Name of the wallet to retrieve
 * @returns Wallet entity or undefined if not found
 */
export function getWallet(config: FrostConfig, walletName: string): WalletEntity | undefined {
  return config.store.state.wallets.get(walletName);
}

/**
 * Checks if a wallet supports a specific feature.
 *
 * @param config - Frost configuration
 * @param walletName - Name of the wallet
 * @param feature - Feature identifier (e.g., "rialo:signTransaction")
 * @returns True if the wallet supports the feature
 */
export function hasFeature(config: FrostConfig, walletName: string, feature: string): boolean {
  const wallet = config.store.state.wallets.get(walletName);
  return wallet?.features.includes(feature) ?? false;
}

/**
 * Checks if a wallet supports the currently configured chain.
 *
 * @param config - Frost configuration
 * @param walletName - Name of the wallet
 * @returns True if the wallet supports the current chain
 */
export function supportsChain(config: FrostConfig, walletName: string): boolean {
  const wallet = config.store.state.wallets.get(walletName);
  const chainId = config.store.state.chainId as IdentifierString;
  return wallet?.chains.includes(chainId) ?? false;
}
